import React, { useState, useEffect } from 'react';
import { api, BASE_URL } from '../api';
import ActionMenu from '../components/ActionMenu';
import MonitoringModal, { PackageStatus } from '../components/MonitoringModal';
import JpegMonitoringModal, { JpegStatus } from '../components/JpegMonitoringModal';
import ExifMonitoringModal, { ExifStatus } from '../components/ExifMonitoringModal';
import LabelMonitoringModal, { LabelStatus } from '../components/LabelMonitoringModal';
import { DigitalFile, Package } from '../types';
import { useNotification } from '../components/NotificationContext';

interface ObjectDashboardProps {
  cantiereId: number;
  dpacCode: string;
  cantiereName: string;
  objectName: string;
  onBack: () => void;
  canEdit?: boolean;
}

const ITEMS_PER_PAGE = 20;

const ObjectDashboard: React.FC<ObjectDashboardProps> = ({ cantiereId, dpacCode, cantiereName, objectName, onBack, canEdit = true }) => {
  const { notify } = useNotification();
  const [files, setFiles] = useState<DigitalFile[]>([]);
  const [jpegFiles, setJpegFiles] = useState<string[]>([]);
  const [dngFiles, setDngFiles] = useState<string[]>([]);
  const [hasCsv, setHasCsv] = useState(false);
  const [hasMetsEcomic, setHasMetsEcomic] = useState(false);
  const [metsFilename, setMetsFilename] = useState('');
  const [hasExifTif, setHasExifTif] = useState(false);
  const [hasExifJpg, setHasExifJpg] = useState(false);
  const [hasExifDng, setHasExifDng] = useState(false);
  const [validationPassed, setValidationPassed] = useState(false);
  const [metadata, setMetadata] = useState<any>({});
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'tiff' | 'jpeg' | 'dng' | 'config' | 'packages'>('tiff');
  const [packages, setPackages] = useState<Package[]>([]);
  const [isShipping, setIsShipping] = useState<number | null>(null);

  // Track editing state
  const [editingFileId, setEditingFileId] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<DigitalFile>>({});

  // Filter state
  const [filters, setFilters] = useState({
    ordinamento: '',
    file_name: '',
    label: '',
    package_name: '',
    max_size_mb: ''
  });

  // Label Upload Modal State
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelFile, setLabelFile] = useState<File | null>(null);
  const [previewResult, setPreviewResult] = useState<{ recognized: number; total: number } | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  // Validation Report Modal State
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationReport, setValidationReport] = useState<any>(null);

  // EXIF Modal State
  const [showExifModal, setShowExifModal] = useState(false);
  const [exifData, setExifData] = useState<any>(null);
  const [exifLoading, setExifLoading] = useState(false);
  const [exifFilename, setExifFilename] = useState('');
  const [exifMonitoringStatus, setExifMonitoringStatus] = useState<ExifStatus | null>(null);
  const [isExifMonitoringOpen, setIsExifMonitoringOpen] = useState(false);

  // Prototype Modal State
  const [showPrototypeModal, setShowPrototypeModal] = useState(false);
  const [lprtSerial, setLprtSerial] = useState('001');
  const [isGeneratingPrototype, setIsGeneratingPrototype] = useState(false);
  // Label Generation State
  // (Old states removed, now using labelMonitoringStatus)

  const [monitoringStatus, setMonitoringStatus] = useState<PackageStatus | null>(null);
  const [isMonitoringOpen, setIsMonitoringOpen] = useState(false);

  // JPEG Monitoring State
  const [jpegMonitoringStatus, setJpegMonitoringStatus] = useState<JpegStatus | null>(null);
  const [isJpegMonitoringOpen, setIsJpegMonitoringOpen] = useState(false);

  // Label Monitoring State
  const [labelMonitoringStatus, setLabelMonitoringStatus] = useState<LabelStatus | null>(null);
  const [isLabelMonitoringOpen, setIsLabelMonitoringOpen] = useState(false);
  const [labelActionStartTime, setLabelActionStartTime] = useState<number>(0);

  // XML Viewer Modal State
  const [showXmlViewerModal, setShowXmlViewerModal] = useState(false);
  const [xmlViewerContent, setXmlViewerContent] = useState('');
  const [xmlViewerLoading, setXmlViewerLoading] = useState(false);
  const [xmlViewerFilename, setXmlViewerFilename] = useState('');
  const [xmlSearchQuery, setXmlSearchQuery] = useState('');
  const [xmlSearchCurrent, setXmlSearchCurrent] = useState(0);
  const xmlSearchInputRef = React.useRef<HTMLInputElement>(null);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxZoom, setLightboxZoom] = useState(1);
  const [exifActionStartTime, setExifActionStartTime] = useState<number>(0);
  const [jpegActionStartTime, setJpegActionStartTime] = useState<number>(0);
  const [algorithmLabelName, setAlgorithmLabelName] = useState<string>('Genera Label (Default)');
  const [draggedFileId, setDraggedFileId] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null); // Index of the element we are hovering over
  const [dragSide, setDragSide] = useState<'left' | 'right' | null>(null);

  // Pagination state (unified for TIFF and JPEG)
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchObjectFiles();

    const interval = setInterval(() => {
      if (!editingFileId) {
        fetchObjectFiles();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [cantiereId, objectName, editingFileId]);

  // Polling for monitoring modal — only depends on isMonitoringOpen and package ID
  // Deliberately NOT including monitoringStatus.is_generating in deps to avoid
  // clearing/recreating the interval on every status update (stale closure issue)
  useEffect(() => {
    if (!isMonitoringOpen || !monitoringStatus?.id) return;
    const pkgId = monitoringStatus.id;
    const interval = setInterval(async () => {
      try {
        const data = await api.get(`/user/package/${pkgId}/status`);
        setMonitoringStatus(data);
        if (!data.is_generating) {
          clearInterval(interval);
          fetchObjectFiles();
        }
      } catch (err: any) {
        clearInterval(interval);
        setMonitoringStatus(prev => prev ? {
          ...prev,
          is_generating: false,
          current_step: 'ERROR',
          last_status_message: err.response?.status === 404
            ? 'Errore critico: generazione interrotta e record eliminato.'
            : 'Errore di connessione durante il monitoraggio.'
        } : null);
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [isMonitoringOpen, monitoringStatus?.id, cantiereId, objectName]);

  // Dedicated polling for EXIF status
  useEffect(() => {
    if (!isExifMonitoringOpen) return;
    const interval = setInterval(async () => {
      try {
        const data = await api.get(`/user/cantiere/${cantiereId}/object/${objectName}/exif-status?t=${Date.now()}`);

        // Ignore status if it's older than when we started the action
        if (data.updated_at) {
          const updateTime = new Date(data.updated_at).getTime();
          if (updateTime < exifActionStartTime) {
            console.log("Ignoring stale EXIF status update");
            return;
          }
        }

        setExifMonitoringStatus(data);
        if (data.current_step === 'COMPLETED' || data.current_step === 'ERROR') {
          clearInterval(interval);
          fetchObjectFiles();
        }
      } catch (err: any) {
        clearInterval(interval);
        console.error("Error polling EXIF status:", err);
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [isExifMonitoringOpen, cantiereId, objectName, exifActionStartTime]);

  // Dedicated polling for JPEG status
  useEffect(() => {
    if (!isJpegMonitoringOpen) return;
    const interval = setInterval(async () => {
      try {
        const data = await api.get(`/user/cantiere/${cantiereId}/object/${objectName}/jpeg-status?t=${Date.now()}`);

        // Ignore status if it's older than when we started the action
        if (data.updated_at) {
          const updateTime = new Date(data.updated_at).getTime();
          if (updateTime < jpegActionStartTime) {
            console.log("Ignoring stale JPEG status update");
            return;
          }
        }

        setJpegMonitoringStatus(data);
        if (data.current_step === 'COMPLETED' || data.current_step === 'ERROR') {
          clearInterval(interval);
          fetchObjectFiles();
        }
      } catch (err: any) {
        clearInterval(interval);
        console.error("Error polling JPEG status:", err);
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [isJpegMonitoringOpen, cantiereId, objectName, jpegActionStartTime]);

  // Dedicated polling for Label status
  useEffect(() => {
    if (!isLabelMonitoringOpen) return;
    const interval = setInterval(async () => {
      try {
        const data = await api.get(`/user/cantiere/${cantiereId}/object/${objectName}/label-status?t=${Date.now()}`);

        if (data.updated_at) {
          const updateTime = new Date(data.updated_at).getTime();
          if (updateTime < labelActionStartTime) {
            console.log("Ignoring stale Label status update");
            return;
          }
        }

        setLabelMonitoringStatus(data);
        if (data.current_step === 'COMPLETED' || data.current_step === 'ERROR') {
          clearInterval(interval);
          fetchObjectFiles();
        }
      } catch (err: any) {
        clearInterval(interval);
        console.error("Error polling Label status:", err);
      }
    }, 1500);
    return () => clearInterval(interval);
  }, [isLabelMonitoringOpen, cantiereId, objectName, labelActionStartTime]);

  useEffect(() => {
    if (activeTab === 'packages') {
      fetchPackages();
    }
  }, [activeTab, cantiereId, objectName]);

  const fetchPackages = async () => {
    try {
      const data = await api.get(`/user/cantiere/${cantiereId}/object/${objectName}/packages`);
      setPackages(data.packages);
    } catch (error: any) {
      notify(error.message || 'Errore recupero pacchetti.', 'error');
    }
  };

  const handleShipPackage = async (packageId: number) => {
    if (!window.confirm('Sei sicuro di voler spedire questo pacchetto a D.PAC?')) return;

    setIsShipping(packageId);
    try {
      await api.post(`/user/package/${packageId}/ship`, {});
      notify('Pacchetto inviato a DPAC con successo!', 'success');
      fetchPackages();
    } catch (error: any) {
      notify(error.message || 'Errore durante l\'invio del pacchetto.', 'error');
    } finally {
      setIsShipping(null);
    }
  };

  const filteredPackages = React.useMemo(() => {
    return packages.filter(pkg => {
      const matchName = !filters.package_name ||
        (pkg.name || '').toLowerCase().includes(filters.package_name.toLowerCase());

      const matchSize = !filters.max_size_mb ||
        (pkg.zip_size_bytes && pkg.zip_size_bytes < (parseFloat(filters.max_size_mb) * 1024 * 1024));

      return matchName && matchSize;
    });
  }, [packages, filters]);

  const fetchObjectFiles = async () => {
    try {
      const data = await api.get(`/user/cantiere/${cantiereId}/object/${objectName}/files?t=${Date.now()}`);
      // Attach object status to files for UI processing
      const filesWithStatus = data.files.map((f: any) => ({
        ...f,
        digital_object_status: data.status
      }));
      setFiles(filesWithStatus);
      setHasCsv(data.has_csv);
      setJpegFiles(data.jpeg_files || []);
      setDngFiles(data.dng_files || []);
      setHasMetsEcomic(data.has_mets_ecomic || false);
      setMetsFilename(data.mets_filename || '');
      setHasExifTif(data.has_exif_tif || false);
      setHasExifJpg(data.has_exif_jpg || false);
      setHasExifDng(data.has_exif_dng || false);
      setValidationPassed(data.validation_passed || false);
      setMetadata(data.metadata || {});
      setAlgorithmLabelName(data.algorithm_label_name || 'Genera Label (Default)');

      // Status updates are handled by dedicated polling loops
      // to avoid race conditions with integrated status.

    } catch (error: any) {
      setMessage(error.message || 'Failed to fetch object files.');
    }
  };

  const handleDragStart = (e: React.DragEvent, fileId: number) => {
    setDraggedFileId(fileId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const side = x < rect.width / 2 ? 'left' : 'right';
    
    setDragOverIndex(index);
    setDragSide(side);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
    setDragSide(null);
  };

  const handleDrop = async (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    if (draggedFileId === null || dragOverIndex === null || dragSide === null) return;

    // Calculate new target position (1-based)
    // If side is 'left', it goes before the element at targetIndex
    // If side is 'right', it goes after the element at targetIndex
    
    // We need to know the index in the ENTIRE list, not just the page.
    // currentJpegFiles is a slice: filteredJpegFiles.slice(indexOfFirstJpeg, indexOfLastJpeg)
    const absoluteTargetIndex = indexOfFirstJpeg + targetIndex;
    
    // The backend uses 1-based indexing for 'ordinamento'.
    // If we want to insert at absoluteTargetIndex:
    // - side 'left' -> index `absoluteTargetIndex`
    // - side 'right' -> index `absoluteTargetIndex + 1`
    let newOrdinamento = absoluteTargetIndex + (dragSide === 'right' ? 1 : 0) + 1;

    // Call the API
    try {
      await api.put(`/user/digital-file/${draggedFileId}`, { ordinamento: newOrdinamento });
      notify('Ordinamento aggiornato con successo', 'success');
      fetchObjectFiles();
    } catch (error: any) {
      notify(error.message || 'Errore durante il riordinamento.', 'error');
    } finally {
      setDraggedFileId(null);
      setDragOverIndex(null);
      setDragSide(null);
    }
  };

  const handleDownloadFile = (filename: string, subdir: string = '') => {
    const token = localStorage.getItem('access_token');
    // For TIFs (root), subdir is empty. For JPEGs, it's 'JPEG'.
    // We need to update backend to handle subdir in download if we want to download JPEGs too.
    // Assuming backend get_file_content handles paths?
    // Route is: /cantiere/<int:cantiere_id>/object/<path:object_name>/file/<path:filename>
    // filename can allow slashes if path converter is used.

    const fullPath = subdir ? `${subdir}/${filename}` : filename;
    window.open(`${BASE_URL}/user/cantiere/${cantiereId}/object/${objectName}/file/${fullPath}?jwt=${token}`, '_blank');
  };

  const handleEditClick = (file: DigitalFile) => {
    setEditingFileId(file.id);
    setEditFormData({ ...file });
  };

  const handleCancelEdit = () => {
    setEditingFileId(null);
    setEditFormData({});
  };

  const handleSaveFile = async (fileId: number) => {
    try {
      await api.put(`/user/digital-file/${fileId}`, editFormData);
      notify('File aggiornato con successo', 'success');
      setEditingFileId(null);
      fetchObjectFiles();
    } catch (error: any) {
      notify(error.message || 'Errore salvataggio file.', 'error');
    }
  };

  const handlePresumedNumbering = async (file: DigitalFile) => {
    if (!file.label) return;

    const bracketedRegex = /\[(\d+)\]([rv])/gi;
    const plainRegex = /(\d+)([rv])/gi;

    let newLabel: string;

    if (bracketedRegex.test(file.label)) {
      // Inverse: [<numero>]r/v → <numero>r/v
      newLabel = file.label.replace(/\[(\d+)\]([rv])/gi, '$1$2');
    } else if (plainRegex.test(file.label)) {
      // Forward: <numero>r/v → [<numero>]r/v
      newLabel = file.label.replace(/(\d+)([rv])/gi, '[$1]$2');
    } else {
      notify('Nessun pattern <numero>r/v trovato nella label', 'info');
      return;
    }

    if (newLabel === file.label) {
      notify('Nessuna modifica necessaria', 'info');
      return;
    }

    try {
      await api.put(`/user/digital-file/${file.id}`, { ...file, label: newLabel });
      notify(`Label aggiornata: ${newLabel}`, 'success');
      fetchObjectFiles();
    } catch (error: any) {
      notify(error.message || 'Errore aggiornamento label.', 'error');
    }
  };

  const handleLabelIncrement = async (file: DigitalFile, amount: number) => {
    if (!file.label) return;

    // Caso 1: "[A-Za-z0-9 ]+: ([0-9]+)[rv]"
    // Caso 2: "[A-Za-z0-9 ]+: \[([0-9]+)\][rv]*"
    const regex2 = /^([A-Za-z0-9 ]+: )\[([0-9]+)\]([rv]?.*)$/;
    const regex1 = /^([A-Za-z0-9 ]+: )([0-9]+)([rv]?.*)$/;

    let newLabel: string = file.label;
    let matched = false;

    // Check Case 2 first
    let match = file.label.match(regex2);
    if (match) {
      const prefix = match[1];
      const num = parseInt(match[2], 10) + amount;
      const suffix = match[3];
      newLabel = `${prefix}[${num}]${suffix}`;
      matched = true;
    } else {
      // Check Case 1
      match = file.label.match(regex1);
      if (match) {
        const prefix = match[1];
        const num = parseInt(match[2], 10) + amount;
        const suffix = match[3];
        newLabel = `${prefix}${num}${suffix}`;
        matched = true;
      }
    }

    if (!matched) {
      notify('Nessun pattern compatibile trovato nella label', 'info');
      return;
    }

    try {
      await api.put(`/user/digital-file/${file.id}`, { ...file, label: newLabel });
      notify(`Label aggiornata: ${newLabel}`, 'success');
      fetchObjectFiles();
    } catch (error: any) {
      notify(error.message || 'Errore aggiornamento label.', 'error');
    }
  };

  const handleLabelSwapRV = async (file: DigitalFile) => {
    if (!file.label) return;

    let newLabel = file.label;
    if (file.label.endsWith('r')) {
      newLabel = file.label.slice(0, -1) + 'v';
    } else if (file.label.endsWith('v')) {
      newLabel = file.label.slice(0, -1) + 'r';
    } else {
      notify('La label non termina con "r" o "v"', 'info');
      return;
    }

    try {
      await api.put(`/user/digital-file/${file.id}`, { ...file, label: newLabel });
      notify(`Label aggiornata: ${newLabel}`, 'success');
      fetchObjectFiles();
    } catch (error: any) {
      notify(error.message || 'Errore aggiornamento label.', 'error');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: type === 'number' ? parseInt(value, 10) : value
    });
  };

  const handleGenerateJpeg = async () => {
    try {
      setJpegMonitoringStatus({
        current_step: 'INIT',
        processed_files: 0,
        total_files: 0,
        is_generating: true,
        last_status_message: 'Avvio generazione...'
      });
      setIsJpegMonitoringOpen(true);
      const data = await api.post(`/user/cantiere/${cantiereId}/object/${objectName}/generate-jpeg`, {});
      if (data.updated_at) {
        setJpegActionStartTime(new Date(data.updated_at).getTime());
      } else {
        setJpegActionStartTime(Date.now());
      }
    } catch (error: any) {
      setJpegMonitoringStatus({
        is_generating: false,
        current_step: 'ERROR',
        processed_files: 0,
        total_files: 0,
        error: error.response?.data?.message || error.message || 'Errore generazione JPEG.'
      });
    }
  };

  const handleExtractExif = async () => {
    try {
      setExifMonitoringStatus({ current_step: 'INIT', is_generating: true, processed_files: 0, total_files: 0, last_status_message: 'Avvio estrazione...' });
      setIsExifMonitoringOpen(true);
      const data = await api.post(`/user/cantiere/${cantiereId}/object/${objectName}/extract-exif`, {});
      if (data.updated_at) {
        setExifActionStartTime(new Date(data.updated_at).getTime());
      } else {
        setExifActionStartTime(Date.now());
      }
    } catch (error: any) {
      notify(error.message || 'Errore estrazione EXIF.', 'error');
    }
  };

  const handleGenerateXml = async () => {
    try {
      const data = await api.post(`/user/cantiere/${cantiereId}/object/${objectName}/generate-xml`, {});
      notify(data.message || 'File XML generato con successo.', 'success');
      fetchObjectFiles(); // Refresh to show the link
    } catch (error: any) {
      notify(error.response?.data?.message || error.message || 'Errore generazione XML.', 'error');
    }
  };

  const handleGeneratePackage = async () => {
    try {
      const data = await api.post(`/user/cantiere/${cantiereId}/object/${objectName}/generate-package`, {});
      if (data.package_id) {
        setMonitoringStatus({ id: data.package_id, is_generating: true, processed_files: 0, total_files: 0, last_status_message: 'Avvio generazione...' });
        setIsMonitoringOpen(true);
      } else {
        notify(data.message || 'Generazione avviata.', 'info');
      }
    } catch (error: any) {
      notify(error.response?.data?.message || error.message || 'Errore generazione pacchetto.', 'error');
    }
  };

  const handleGeneratePrototype = async () => {
    if (!lprtSerial || lprtSerial.length > 3) {
      alert("Il seriale LPRT deve essere di massimo 3 cifre");
      return;
    }

    setIsGeneratingPrototype(true);
    try {
      const data = await api.post(`/user/cantiere/${cantiereId}/object/${objectName}/generate-prototype`, { lprt_serial: lprtSerial });
      setShowPrototypeModal(false);
      if (data.package_id) {
        setMonitoringStatus({ id: data.package_id, is_generating: true, processed_files: 0, total_files: 0, last_status_message: 'Avvio generazione prototipo...' });
        setIsMonitoringOpen(true);
      } else {
        notify(data.message || 'Prototipo generato con successo.', 'info');
        fetchObjectFiles();
      }
    } catch (error: any) {
      notify(error.response?.data?.message || error.message || 'Errore durante la generazione del prototipo.', 'error');
    } finally {
      setIsGeneratingPrototype(false);
    }
  };

  const handleLabelFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLabelFile(file);
    setPreviewResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const data = await api.post(`/user/cantiere/${cantiereId}/object/${objectName}/preview-labels`, formData);
      setPreviewResult(data);
    } catch (error: any) {
      alert(error.message || 'Errore durante la preview del CSV');
    }
  };

  const handleApplyLabels = async () => {
    if (!labelFile) return;

    setIsApplying(true);
    const formData = new FormData();
    formData.append('file', labelFile);

    try {
      const data = await api.post(`/user/cantiere/${cantiereId}/object/${objectName}/apply-labels`, formData);
      alert(data.message);
      setShowLabelModal(false);
      setLabelFile(null);
      setPreviewResult(null);
      fetchObjectFiles();
    } catch (error: any) {
      alert(error.message || 'Errore durante l\'applicazione delle label');
    } finally {
      setIsApplying(false);
    }
  };

  const handleGenerateLabels = async () => {
    try {
      setLabelMonitoringStatus({ current_step: 'INIT', is_generating: true, processed_files: 0, total_files: 0, last_status_message: 'Avvio generazione...' });
      setIsLabelMonitoringOpen(true);
      setLabelActionStartTime(Date.now());
      
      const data = await api.post(`/user/cantiere/${cantiereId}/object/${objectName}/generate-labels`, {});
      // Successively, the useEffect polling will handle the UI updates
    } catch (error: any) {
      setLabelMonitoringStatus({
        is_generating: false,
        current_step: 'ERROR',
        processed_files: 0,
        total_files: 0,
        error: error.response?.data?.message || error.message || 'Errore generazione label.'
      });
    }
  };

  const handleViewValidationReport = async () => {
    try {
      const data = await api.get(`/user/cantiere/${cantiereId}/object/${objectName}/validation-report`);
      setValidationReport(data);
      setShowValidationModal(true);
    } catch (error: any) {
      alert(error.message || 'Errore durante il recupero del report di validazione');
    }
  };

  const handleViewXmlFile = async () => {
    setXmlViewerLoading(true);
    setXmlViewerContent('');
    setXmlViewerFilename(metsFilename || '');
    setShowXmlViewerModal(true);
    try {
      const token = localStorage.getItem('access_token');
      const params = metsFilename ? `?jwt=${token}&filename=${encodeURIComponent(metsFilename)}` : `?jwt=${token}`;
      const resp = await fetch(
        `${BASE_URL}/user/cantiere/${cantiereId}/object/${objectName}/xml-content${params}`
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const detectedFilename = resp.headers.get('X-Xml-Filename');
      if (detectedFilename) setXmlViewerFilename(detectedFilename);
      const text = await resp.text();
      setXmlViewerContent(text);
    } catch (error: any) {
      setXmlViewerContent(`Errore durante il caricamento del file: ${error.message}`);
    } finally {
      setXmlViewerLoading(false);
    }
  };


  const handleViewExif = async (filename: string) => {
    setExifFilename(filename);
    setExifLoading(true);
    setShowExifModal(true);
    setExifData(null);

    try {
      const data = await api.get(`/user/cantiere/${cantiereId}/object/${objectName}/file/${filename}/exif`);
      setExifData(data.exif);
    } catch (error: any) {
      // Show detailed error message if debug info is available
      let errorMsg = error.message || 'Errore durante il recupero dei dati EXIF';
      if (error.debug) {
        console.error('EXIF Debug Info:', error.debug);
        errorMsg += '\n\nDebug Info:\n' + JSON.stringify(error.debug, null, 2);
      }
      alert(errorMsg);
      setShowExifModal(false);
    } finally {
      setExifLoading(false);
    }
  };


  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const getFileUrl = (filename: string, subdir: string = '') => {
    const token = localStorage.getItem('access_token');
    const fullPath = subdir ? `${subdir}/${filename}` : filename;
    return `${BASE_URL}/user/cantiere/${cantiereId}/object/${objectName}/file/${fullPath}?jwt=${token}`;
  };

  // Helper function to check if JPEG exists for a TIFF file
  const hasJpeg = (tiffFileName: string): boolean => {
    if (jpegFiles.length === 0) return false;
    const baseName = tiffFileName.replace(/\.tiff?$/i, '');
    const jpegName = `${baseName}.jpg`;
    return jpegFiles.some(jpeg => jpeg.toLowerCase() === jpegName.toLowerCase());
  };

  const handleViewJpeg = (tiffFileName: string) => {
    const baseName = tiffFileName.replace(/\.tiff?$/i, '');
    const jpegName = `${baseName}.jpg`;
    // Open lightbox instead of download
    const idx = filteredJpegFiles.findIndex(f => f.toLowerCase() === jpegName.toLowerCase());
    if (idx >= 0) {
      setLightboxIndex(idx);
      setLightboxZoom(1);
      setLightboxOpen(true);
    } else {
      handleDownloadFile(jpegName, 'JPEG');
    }
  };

  const handleDownloadConfigExcel = () => {
    const token = localStorage.getItem('access_token');
    window.open(`${BASE_URL}/user/cantiere/${cantiereId}/object/${objectName}/config-excel?jwt=${token}`, '_blank');
  };

  const handleDownloadLabelsCsv = () => {
    const token = localStorage.getItem('access_token');
    window.open(`${BASE_URL}/user/cantiere/${cantiereId}/object/${objectName}/download-labels?jwt=${token}`, '_blank');
  };

  const totalJpegPages = Math.ceil(jpegFiles.length / ITEMS_PER_PAGE);

  // Filtered files logic
  const filteredTiffFiles = React.useMemo(() => {
    return files.filter(f => {
      return (
        (f.ordinamento?.toString() || '').includes(filters.ordinamento) &&
        f.file_name.toLowerCase().includes(filters.file_name.toLowerCase()) &&
        (f.label || '').toLowerCase().includes(filters.label.toLowerCase())
      );
    });
  }, [files, filters]);

  // Pagination for TIFFs
  const totalFilteredTiffPages = Math.ceil(filteredTiffFiles.length / ITEMS_PER_PAGE);
  const indexOfLastTiff = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstTiff = indexOfLastTiff - ITEMS_PER_PAGE;
  const currentTiffFiles = filteredTiffFiles.slice(indexOfFirstTiff, indexOfLastTiff);

  const filteredJpegFiles = React.useMemo(() => {
    return filteredTiffFiles
      .map(tiff => {
        const baseName = tiff.file_name.replace(/\.tiff?$/i, '');
        const jpegName = `${baseName}.jpg`;
        return jpegFiles.find(jf => jf.toLowerCase() === jpegName.toLowerCase());
      })
      .filter((jf): jf is string => jf !== undefined);
  }, [filteredTiffFiles, jpegFiles]);

  // Calculate pagination for JPEGs using filtered list
  const indexOfLastJpeg = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstJpeg = indexOfLastJpeg - ITEMS_PER_PAGE;
  const currentJpegFiles = filteredJpegFiles.slice(indexOfFirstJpeg, indexOfLastJpeg);
  const totalFilteredJpegPages = Math.ceil(filteredJpegFiles.length / ITEMS_PER_PAGE);

  const calculateStato = (): string => {
    // Check if pacchettizzato (based on existence of package name in response)
    // Actually, calculate_object_status on backend handles this now.
    // If the object has a package, the backend will return 'Pacchettizzato' in the status.
    // However, fetchObjectFiles doesn't currently return the status of the DigitalObject directly, 
    // it returns files and metadata. 
    // Wait, the status is part of the DigitalObject model.
    // Let's re-examine fetchObjectFiles.
    return files.length > 0 ? (files[0] as any).digital_object_status || 'Draft' : 'Draft';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completo': return 'var(--success-color)';
      case 'In lavorazione': return 'var(--warning-color)';
      case 'Manca lotto': return 'var(--error-color)';
      case 'Pacchettizzato': return '#2196f3';
      default: return 'rgba(0,0,0,0.4)';
    }
  };

  const isGenerationDisabled = files.length === 0 || files.some(f => !f.label || !f.label.trim());

  const hasDuplicateOrdinamento = () => {
    const values = files.map(f => f.ordinamento).filter(v => v !== undefined && v !== null);
    return new Set(values).size !== values.length;
  };

  const isPackageGenerationDisabled =
    isGenerationDisabled ||
    !hasMetsEcomic ||
    !validationPassed ||
    jpegFiles.length === 0 ||
    files.length !== jpegFiles.length ||
    !files.every(f => hasJpeg(f.file_name)) ||
    hasDuplicateOrdinamento();

  const getPackageDisabledTitle = () => {
    if (files.length === 0) return "Nessun file presente";
    if (files.some(f => !f.label || !f.label.trim())) return "Compila la Label per tutti i file";
    if (hasDuplicateOrdinamento()) return "I numeri di sequenza (Ord.) devono essere unici";
    if (!hasMetsEcomic) return "File XML non generato";
    if (!validationPassed) return "Validazione XML fallita (esito deve essere true)";
    if (jpegFiles.length === 0) return "Directory JPEG mancante o vuota";
    if (files.length !== jpegFiles.length) return `Numero di file JPEG (${jpegFiles.length}) diverso dal numero di file TIFF (${files.length})`;
    if (!files.every(f => hasJpeg(f.file_name))) return "Mancano i file JPEG corrispondenti per alcuni file TIFF";
    return "";
  };

  return (
    <div className="container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button className="btn btn-text" onClick={onBack}>← Back</button>
        <h2 style={{ margin: 0 }}>{objectName}</h2>
        <span style={{ fontSize: '0.9rem', color: 'rgba(0,0,0,0.5)' }}>{dpacCode} - {cantiereName}</span>
        {files.length > 0 && (
          <div style={{ marginLeft: 'auto' }}>
            <span style={{
              color: getStatusColor(calculateStato()),
              backgroundColor: getStatusColor(calculateStato()) + '1A',
              padding: '6px 12px',
              borderRadius: '4px',
              border: `1px solid ${getStatusColor(calculateStato())}`,
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}>
              {calculateStato()}
            </span>
          </div>
        )}
      </div>

      {/* Legacy local message display removed */}

      <div style={{ marginBottom: '24px' }}>
        <div className="card" style={{ padding: '16px' }}>
          {canEdit && (
            <div className="actions-grid">
              <button
                className="btn btn-secondary"
                onClick={handleGenerateJpeg}
              >
                GENERA JPEG
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleExtractExif}
              >
                ESTRAI EXIF
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleGenerateLabels}
                disabled={labelMonitoringStatus?.is_generating || calculateStato() === "Pacchettizzato"}
                title={calculateStato() === "Pacchettizzato" ? "Impossibile generare label per oggetti già pacchettizzati" : `Algoritmo: ${algorithmLabelName}`}
              >
                {labelMonitoringStatus?.is_generating ? 'GENERAZIONE...' : 'GENERA LABEL'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleDownloadLabelsCsv}
              >
                SCARICA LABEL
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setShowLabelModal(true)}
              >
                CARICA LABEL
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleGenerateXml}
                disabled={isGenerationDisabled}
                title={isGenerationDisabled ? "Compila la Label per tutti i file per procedere" : ""}
                style={isGenerationDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                Genera XML
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleGeneratePackage}
                disabled={isPackageGenerationDisabled}
                title={getPackageDisabledTitle()}
                style={isPackageGenerationDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                Genera Pacchetto
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setShowPrototypeModal(true)}
                disabled={isPackageGenerationDisabled}
                title={isPackageGenerationDisabled ? getPackageDisabledTitle() : "Genera Prototipo"}
                style={isPackageGenerationDisabled ? { opacity: 0.5, cursor: 'not-allowed', backgroundColor: '#6200ee' } : { backgroundColor: '#6200ee' }}
              >
                GENERA PROTOTIPO
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Status Info Boxes */}
      <div className="stats-grid" style={{ marginBottom: '24px' }}>
        <div className="card" style={{ padding: '12px 16px', textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 6px 0', color: '#666', fontSize: '0.8rem' }}>Numero TIFF</h4>
          <div style={{ fontSize: '1.6rem', fontWeight: 'bold' }}>{files.length}</div>
        </div>
        <div className="card" style={{ padding: '12px 16px', textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 6px 0', color: '#666', fontSize: '0.8rem' }}>Numero JPEG</h4>
          <div style={{ fontSize: '1.6rem', fontWeight: 'bold' }}>{jpegFiles.length}</div>
        </div>
        <div className="card" style={{ padding: '12px 16px', textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 6px 0', color: '#666', fontSize: '0.8rem' }}>Numero DNG</h4>
          <div style={{ fontSize: '1.6rem', fontWeight: 'bold' }}>{dngFiles.length}</div>
        </div>
        <div className="card" style={{ padding: '12px 16px', textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 6px 0', color: '#666', fontSize: '0.8rem' }}>METS ECOMIC</h4>
          <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
            {hasMetsEcomic ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <button
                  onClick={handleViewXmlFile}
                  style={{
                    background: 'none', border: 'none', padding: 0,
                    color: 'var(--primary-color)', textDecoration: 'underline',
                    cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit'
                  }}
                  title="Visualizza XML"
                >
                  xml
                </button>
                {validationPassed ? (
                  <span
                    style={{ color: '#28a745', fontSize: '1.5rem', cursor: 'pointer' }}
                    title="Validation passed - Click for details"
                    onClick={handleViewValidationReport}
                  >
                    ✓
                  </span>
                ) : (
                  <span
                    style={{ color: '#dc3545', fontSize: '1.5rem', cursor: 'pointer' }}
                    title="Validation failed - Click for details"
                    onClick={handleViewValidationReport}
                  >
                    ✗
                  </span>
                )}
              </div>
            ) : (
              <span style={{ color: '#999' }}>non generato</span>
            )}
          </div>
        </div>
        <div className="card" style={{ padding: '12px 16px', textAlign: 'center' }}>
          <h4 style={{ margin: '0 0 6px 0', color: '#666', fontSize: '0.8rem' }}>EXIF</h4>
          <div style={{ fontSize: '1.1rem', fontWeight: 'bold', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {hasExifTif && (
              <a
                href={`${BASE_URL}/user/cantiere/${cantiereId}/object/${objectName}/file/exif_tif.xlsx?jwt=${localStorage.getItem('access_token')}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--primary-color)', textDecoration: 'underline', fontSize: '1rem' }}
              >
                EXIF TIF
              </a>
            )}
            {hasExifJpg && (
              <a
                href={`${BASE_URL}/user/cantiere/${cantiereId}/object/${objectName}/file/exif_jpg.xlsx?jwt=${localStorage.getItem('access_token')}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--primary-color)', textDecoration: 'underline', fontSize: '1rem' }}
              >
                EXIF JPG
              </a>
            )}
            {hasExifDng && (
              <a
                href={`${BASE_URL}/user/cantiere/${cantiereId}/object/${objectName}/file/exif_dng.xlsx?jwt=${localStorage.getItem('access_token')}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--primary-color)', textDecoration: 'underline', fontSize: '1rem' }}
              >
                EXIF DNG
              </a>
            )}
            {!hasExifTif && !hasExifJpg && !hasExifDng && (
              <span style={{ color: '#999', fontSize: '1rem' }}>Non disponibile</span>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: '20px', borderBottom: '1px solid #eee', marginBottom: '16px' }}>
          <div
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              borderBottom: activeTab === 'tiff' ? '2px solid var(--primary-color)' : 'none',
              fontWeight: activeTab === 'tiff' ? 'bold' : 'normal',
              color: activeTab === 'tiff' ? 'var(--primary-color)' : 'inherit'
            }}
            onClick={() => setActiveTab('tiff')}
          >
            File TIFF
          </div>
          <div
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              borderBottom: activeTab === 'jpeg' ? '2px solid var(--primary-color)' : 'none',
              fontWeight: activeTab === 'jpeg' ? 'bold' : 'normal',
              color: activeTab === 'jpeg' ? 'var(--primary-color)' : 'inherit'
            }}
            onClick={() => setActiveTab('jpeg')}
          >
            File JPEG
          </div>
          <div
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              borderBottom: activeTab === 'dng' ? '2px solid var(--primary-color)' : 'none',
              fontWeight: activeTab === 'dng' ? 'bold' : 'normal',
              color: activeTab === 'dng' ? 'var(--primary-color)' : 'inherit'
            }}
            onClick={() => setActiveTab('dng')}
          >
            File DNG
          </div>
          <div
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              borderBottom: activeTab === 'config' ? '2px solid var(--primary-color)' : 'none',
              fontWeight: activeTab === 'config' ? 'bold' : 'normal',
              color: activeTab === 'config' ? 'var(--primary-color)' : 'inherit'
            }}
            onClick={() => setActiveTab('config')}
          >
            CONFIGURAZIONE
          </div>
          <div
            style={{
              padding: '8px 16px',
              cursor: 'pointer',
              borderBottom: activeTab === 'packages' ? '2px solid var(--primary-color)' : 'none',
              fontWeight: activeTab === 'packages' ? 'bold' : 'normal',
              color: activeTab === 'packages' ? 'var(--primary-color)' : 'inherit'
            }}
            onClick={() => setActiveTab('packages')}
          >
            PACCHETTI
          </div>
        </div>

        {activeTab === 'tiff' && (
          <>
            <table className="table-responsive">
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>Ord.</th>
                  <th>Nome File (FS)</th>
                  <th style={{ width: '20%' }}>Label</th>
                  <th style={{ width: '150px' }}>Azioni</th>
                </tr>
                <tr>
                  <th>
                    <input
                      type="text"
                      placeholder="Cerca..."
                      style={{ width: '100%', padding: '4px', fontSize: '0.8rem', fontWeight: 'normal' }}
                      value={filters.ordinamento}
                      onChange={(e) => { setFilters({ ...filters, ordinamento: e.target.value }); setCurrentPage(1); }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="Cerca..."
                      style={{ width: '100%', padding: '4px', fontSize: '0.8rem', fontWeight: 'normal' }}
                      value={filters.file_name}
                      onChange={(e) => { setFilters({ ...filters, file_name: e.target.value }); setCurrentPage(1); }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="Cerca..."
                      style={{ width: '100%', padding: '4px', fontSize: '0.8rem', fontWeight: 'normal' }}
                      value={filters.label}
                      onChange={(e) => { setFilters({ ...filters, label: e.target.value }); setCurrentPage(1); }}
                    />
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {currentTiffFiles.map((file) => (
                  <tr key={file.id}>
                    {editingFileId === file.id ? (
                      <>
                        <td><input type="number" min="1" name="ordinamento" value={editFormData.ordinamento || 1} onChange={handleInputChange} style={{ width: '60px' }} /></td>
                        <td><input type="text" name="file_name" value={editFormData.file_name || ''} onChange={handleInputChange} /></td>
                        <td><input type="text" name="label" value={editFormData.label || ''} onChange={handleInputChange} /></td>
                        <td>
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button className="btn btn-primary" style={{ padding: '4px 8px', fontSize: '0.8rem' }} onClick={() => handleSaveFile(file.id)}>Salva</button>
                            <button className="btn btn-text" style={{ padding: '4px 8px', fontSize: '0.8rem' }} onClick={handleCancelEdit}>Annulla</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{file.ordinamento || 1}</td>
                        <td>
                          {file.file_name}
                          <button className="btn btn-link" style={{ marginLeft: '10px', fontSize: '1rem' }} onClick={() => handleDownloadFile(file.file_name)} title="Download"><i className="fas fa-download"></i></button>
                        </td>
                        <td>{file.label}</td>
                        <td>
                          <ActionMenu actions={
                            canEdit ? [
                              { label: 'Modifica', icon: 'fas fa-pen-to-square', onClick: () => handleEditClick(file) },
                              {
                                label: 'Visualizza JPEG',
                                icon: 'fas fa-magnifying-glass',
                                onClick: () => handleViewJpeg(file.file_name),
                                disabled: !hasJpeg(file.file_name),
                                title: !hasJpeg(file.file_name) ? "JPEG non disponibile" : undefined
                              },
                              { label: 'Numerazione presunta', icon: 'fas fa-code', onClick: () => handlePresumedNumbering(file) },
                              { label: '+', icon: 'fas fa-plus', onClick: () => handleLabelIncrement(file, 1), title: 'Incrementa numero nella label' },
                              { label: '-', icon: 'fas fa-minus', onClick: () => handleLabelIncrement(file, -1), title: 'Decrementa numero nella label' },
                              { label: 'r/v', icon: 'fas fa-sync', onClick: () => handleLabelSwapRV(file), title: 'Scambia r e v a fine label' },
                              { label: 'Visualizza EXIF', icon: 'fas fa-clipboard-list', onClick: () => handleViewExif(file.file_name) },
                            ] : [
                              {
                                label: 'Visualizza JPEG',
                                icon: 'fas fa-magnifying-glass',
                                onClick: () => handleViewJpeg(file.file_name),
                                disabled: !hasJpeg(file.file_name),
                                title: !hasJpeg(file.file_name) ? "JPEG non disponibile" : undefined
                              },
                              { label: 'Visualizza EXIF', icon: 'fas fa-clipboard-list', onClick: () => handleViewExif(file.file_name) },
                            ]
                          } />
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredTiffFiles.length === 0 && (
              <p style={{ color: 'rgba(0,0,0,0.4)', padding: '20px', textAlign: 'center' }}>
                {files.length === 0 ? "Nessun file .tif trovato." : "Nessun file corrisponde ai filtri."}
              </p>
            )}

            {/* Pagination Controls for TIFFs */}
            {totalFilteredTiffPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center', marginTop: '20px' }}>
                <button
                  className="btn btn-secondary"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Prev
                </button>
                <span>Pagina {currentPage} di {totalFilteredTiffPages}</span>
                <button
                  className="btn btn-secondary"
                  disabled={currentPage === totalFilteredTiffPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {activeTab === 'jpeg' && (
          <div>
            <div style={{ padding: '16px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>File JPEG</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Cerca JPEG..."
                  className="form-control"
                  value={filters.file_name}
                  onChange={(e) => { setFilters({ ...filters, file_name: e.target.value }); setCurrentPage(1); }}
                  style={{ padding: '4px 8px', fontSize: '0.9rem' }}
                />
                <span style={{ fontSize: '0.9rem', color: '#666' }}>{jpegFiles.length} file</span>
              </div>
            </div>

            {currentJpegFiles.length > 0 ? (
              <>
                <div className="jpeg-grid" key={`jpeg-page-${currentPage}`}>
                  {currentJpegFiles.map((file, index) => {
                    const baseName = file.replace(/\.(jpg|jpeg)$/i, '');
                    const matchingTiff = files.find(f => f.file_name.replace(/\.tiff?$/i, '') === baseName);
                    const label = matchingTiff?.label;
                    const fileId = matchingTiff?.id || 0;

                    return (
                      <div
                        key={index}
                        className={`jpeg-item-container ${draggedFileId === fileId ? 'dragging' : ''}`}
                        draggable={canEdit}
                        onDragStart={(e) => handleDragStart(e, fileId)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        style={{ display: 'flex' }}
                      >
                        {dragOverIndex === index && dragSide === 'left' && (
                          <div className="drop-indicator left" />
                        )}
                        <div className="card" style={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: '10px', minHeight: '230px', width: '100%', margin: 0 }}>
                          <div style={{ position: 'relative', width: '100%', paddingTop: '75%', backgroundColor: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
                            <img
                              src={getFileUrl(file, 'JPEG')}
                              alt={file}
                              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                              onClick={() => {
                                // compute index in filteredJpegFiles (not just currentPage)
                                const idx = filteredJpegFiles.findIndex(f => f === file);
                                setLightboxIndex(idx >= 0 ? idx : 0);
                                setLightboxZoom(1);
                                setLightboxOpen(true);
                              }}
                              onError={(e) => { (e.target as any).src = 'https://via.placeholder.com/150?text=No+Image'; }}
                            />
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <div style={{ fontWeight: 'bold', fontSize: '0.85rem', wordBreak: 'break-all', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={file}>
                              {file}
                            </div>
                            {label && (
                              <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>
                                Label: {label}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                            <button className="btn btn-sm btn-link" onClick={() => handleDownloadFile(file, 'JPEG')} title="Scarica"><i className="fas fa-download"></i></button>
                          </div>
                        </div>
                        {dragOverIndex === index && dragSide === 'right' && (
                          <div className="drop-indicator right" />
                        )}
                      </div>
                    );
                  })}
                </div>

                {totalFilteredJpegPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', padding: '20px', borderTop: '1px solid #eee' }}>
                    <button
                      className="btn btn-secondary"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      Prev
                    </button>
                    <span style={{ fontWeight: 'bold' }}>Pagina {currentPage} di {totalFilteredJpegPages}</span>
                    <button
                      className="btn btn-secondary"
                      disabled={currentPage === totalFilteredJpegPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                Nessun file JPEG trovato.
              </div>
            )}
          </div>
        )}

        {activeTab === 'dng' && (
          <table className="table-responsive">
            <thead>
              <tr>
                <th>Nome File (DNG)</th>
                <th style={{ width: '150px' }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {dngFiles.length > 0 ? dngFiles.map((dngName, index) => (
                <tr key={index}>
                  <td>
                    <i className="fas fa-file-image" style={{ marginRight: '8px', color: '#90a4ae' }}></i>
                    {dngName}
                  </td>
                  <td>
                    <button
                      className="btn btn-link"
                      style={{ fontSize: '1rem' }}
                      onClick={() => handleDownloadFile(dngName, 'DNG')}
                      title="Scarica"
                    >
                      <i className="fas fa-download"></i>
                    </button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={2} style={{ textAlign: 'center', color: '#999' }}>Nessun file DNG trovato.</td></tr>
              )}
            </tbody>
          </table>
        )}

        {activeTab === 'config' && (
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Configurazione Oggetto Digitale</h3>
              <button className="btn btn-primary" onClick={handleDownloadConfigExcel}>SCARICA EXCEL</button>
            </div>

            <table className="table-responsive" style={{ border: '1px solid #eee' }}>
              <thead>
                <tr>
                  <th style={{ backgroundColor: '#f9f9f9', width: '30%' }}>Nome Dato</th>
                  <th style={{ backgroundColor: '#f9f9f9' }}>Valore</th>
                </tr>
              </thead>
              <tbody>
                <tr><td><strong>Object ID</strong></td><td>{metadata.object_id || '-'}</td></tr>
                <tr><td><strong>Logical ID</strong></td><td>{metadata.logical_id || '-'}</td></tr>
                <tr><td><strong>Management ID</strong></td><td>{metadata.management_id || '-'}</td></tr>
                <tr><td><strong>Dossier ID</strong></td><td>{metadata.dossier_id || '-'}</td></tr>
                <tr><td><strong>Titolo</strong></td><td>{metadata.title || '-'}</td></tr>
                <tr><td><strong>Data Pubblicazione</strong></td><td>{metadata.publication_date || '-'}</td></tr>
                <tr><td><strong>Forma Fisica</strong></td><td>{metadata.physical_form || '-'}</td></tr>
                <tr><td><strong>Periodico Anno</strong></td><td>{metadata.periodical_year || '0000'}</td></tr>
                <tr><td><strong>Periodico Mese</strong></td><td>{metadata.periodical_month || '00'}</td></tr>
                <tr><td><strong>Periodico Giorno</strong></td><td>{metadata.periodical_day || '00'}</td></tr>
                <tr><td><strong>Periodico Annata</strong></td><td>{metadata.periodical_vol || '-'}</td></tr>
                <tr><td><strong>Periodico Numero</strong></td><td>{metadata.periodical_issue || '-'}</td></tr>
                <tr><td><strong>Supplemento/Indice</strong></td><td>{metadata.periodical_type || 'Nessuno'}</td></tr>
                <tr><td><strong>Periodico part-data</strong></td><td>{metadata.periodical_part_data || '-'}</td></tr>
                <tr><td><strong>Periodico part-detail-number</strong></td><td>{metadata.periodical_part_detail_number || '-'}</td></tr>
                <tr><td><strong>Access Condition</strong></td><td>{metadata.access_condition || '-'}</td></tr>
                <tr><td><strong>Data Inizio</strong></td><td>{metadata.date_issued_start || '-'}</td></tr>
                <tr><td><strong>Data Fine</strong></td><td>{metadata.date_issued_end || '-'}</td></tr>
                <tr><td><strong>Manoscritto Forma</strong></td><td>{metadata.manoscritto_form || '-'}</td></tr>
                <tr><td><strong>Manoscritto Consistenza</strong></td><td>{metadata.manoscritto_extent || '-'}</td></tr>
                <tr><td><strong>Collezione</strong></td><td>{metadata.collezione || '-'}</td></tr>
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'packages' && (
          <div style={{ padding: '20px' }}>
            <h3 style={{ marginBottom: '16px' }}>Pacchetti Associati</h3>
            <table className="table-responsive">
              <thead>
                <tr>
                  <th>Nome Pacchetto</th>
                  <th>Tipo</th>
                  <th>Dimensione ZIP</th>
                  <th>Stato</th>
                  <th style={{ width: '150px' }}>Azioni</th>
                </tr>
                <tr>
                  <th>
                    <input
                      type="text"
                      placeholder="Cerca pacchetto..."
                      style={{ width: '100%', padding: '4px', fontSize: '0.8rem', fontWeight: 'normal' }}
                      value={filters.package_name}
                      onChange={(e) => setFilters({ ...filters, package_name: e.target.value })}
                    />
                  </th>
                  <th></th>
                  <th>
                    <input
                      type="number"
                      placeholder="Max MB..."
                      style={{ width: '100%', padding: '4px', fontSize: '0.8rem', fontWeight: 'normal' }}
                      value={filters.max_size_mb}
                      onChange={(e) => setFilters({ ...filters, max_size_mb: e.target.value })}
                    />
                  </th>
                  <th></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredPackages.length > 0 ? filteredPackages.map((pkg) => (
                  <tr key={pkg.id} style={{
                    backgroundColor: pkg.row_color || 'inherit'
                  }}>
                    <td>
                      <div><strong>{pkg.name}</strong></div>
                      <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '2px' }}>
                        {pkg.spedito_at && (
                          <div>Inviato: {new Date(pkg.spedito_at).toLocaleString('it-IT')}</div>
                        )}
                        {pkg.archiviato_at && (
                          <div>Archiviato: {new Date(pkg.archiviato_at).toLocaleString('it-IT')}</div>
                        )}
                      </div>
                    </td>
                    <td>{pkg.is_prototype ? 'PROTOTIPO' : 'PRODUZIONE'}</td>
                    <td>{pkg.zip_size || 'N/A'}</td>
                    <td>
                      {pkg.trasferito ? (
                        <span className="badge badge-warning">TRASFERITO</span>
                      ) : pkg.in_spedizione ? (
                        <span className="badge badge-success">IN SPEDIZIONE</span>
                      ) : (
                        <span className="badge badge-secondary">DISPONIBILE</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={pkg.trasferito || pkg.in_spedizione || isShipping !== null}
                          onClick={() => handleShipPackage(pkg.id)}
                        >
                          <i className="fas fa-upload" style={{ marginRight: '6px' }}></i>
                          {isShipping === pkg.id ? 'Invio...' : 'Invia a DPAC'}
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: '#999', padding: '20px' }}>
                      Nessun pacchetto generato per questo oggetto.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal for Label Upload */}
      {showLabelModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '400px' }}>
            <h2>Carica Label</h2>
            <div className="form-group">
              <label>Seleziona file CSV (ordinamento, nome_file, label)</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleLabelFileChange}
                style={{ marginTop: '8px' }}
              />
            </div>

            {previewResult && (
              <div style={{ margin: '20px 0', padding: '12px', background: '#f0f7ff', borderRadius: '4px' }}>
                <strong>Risultato:</strong> riconosciuti {previewResult.recognized} file su {previewResult.total} totali
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                className="btn btn-text"
                onClick={() => {
                  setShowLabelModal(false);
                  setLabelFile(null);
                  setPreviewResult(null);
                }}
              >
                Annulla
              </button>
              <button
                className="btn btn-primary"
                disabled={!previewResult || isApplying}
                onClick={handleApplyLabels}
              >
                {isApplying ? 'Applicazione...' : 'APPLICA'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Validation Report */}
      {showValidationModal && (
        <div className="modal-overlay" onClick={() => setShowValidationModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
            <h2>Report di Validazione</h2>
            <div style={{
              background: '#f5f5f5',
              padding: '16px',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '60vh',
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              whiteSpace: 'pre-wrap'
            }}>
              {validationReport ? JSON.stringify(validationReport, null, 2) : 'Caricamento...'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button className="btn btn-primary" onClick={() => setShowValidationModal(false)}>
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for EXIF Data */}
      {showExifModal && (
        <div className="modal-overlay" onClick={() => setShowExifModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%' }}>
            <h2>EXIF Metadata - {exifFilename}</h2>
            {exifLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>Caricamento dati EXIF...</p>
              </div>
            ) : exifData ? (
              <div style={{
                maxHeight: '60vh',
                overflow: 'auto',
                marginTop: '16px'
              }}>
                <table className="table-responsive" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '40%' }}>Campo</th>
                      <th>Valore</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(exifData).map(([key, value]) => (
                      <tr key={key}>
                        <td style={{ fontWeight: 'bold', color: '#555' }}>{key}</td>
                        <td style={{ wordBreak: 'break-word' }}>
                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                Nessun dato EXIF disponibile
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button className="btn btn-primary" onClick={() => setShowExifModal(false)}>
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Prototype Generation */}
      {showPrototypeModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '400px' }}>
            <h2>Genera Prototipo</h2>
            <div className="form-group">
              <label>Inserisci Seriale LPRT (3 cifre)</label>
              <input
                type="text"
                maxLength={3}
                placeholder="001"
                className="form-control"
                value={lprtSerial}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  setLprtSerial(val);
                }}
                style={{ marginTop: '8px', fontSize: '1.2rem', textAlign: 'center', letterSpacing: '2px' }}
              />
              <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '8px' }}>
                Il formato sarà: {dpacCode}_LPRT{lprtSerial.padStart(3, '0')}_PK...
              </p>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <button
                className="btn btn-text"
                onClick={() => {
                  setShowPrototypeModal(false);
                }}
              >
                Annulla
              </button>
              <button
                className="btn btn-primary"
                style={{ backgroundColor: '#6200ee' }}
                disabled={!lprtSerial || isGeneratingPrototype}
                onClick={handleGeneratePrototype}
              >
                {isGeneratingPrototype ? 'Generazione...' : 'GENERA PROTOTIPO'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Label Error Modal */}
      {isLabelMonitoringOpen && (
        <LabelMonitoringModal
          status={labelMonitoringStatus}
          onClose={() => setIsLabelMonitoringOpen(false)}
          algorithmName={algorithmLabelName}
        />
      )}

      {isMonitoringOpen && (
        <MonitoringModal
          status={monitoringStatus}
          onClose={() => setIsMonitoringOpen(false)}
        />
      )}

      {/* XML Viewer Modal */}
      {showXmlViewerModal && (() => {
        const lines = xmlViewerContent.split('\n');
        const q = xmlSearchQuery.trim().toLowerCase();
        // Compute matched line indices
        const matchedLines: number[] = q
          ? lines.reduce<number[]>((acc, l, i) => { if (l.toLowerCase().includes(q)) acc.push(i); return acc; }, [])
          : [];
        const totalMatches = matchedLines.length;
        const currentMatchLineIdx = totalMatches > 0 ? matchedLines[xmlSearchCurrent % totalMatches] : -1;

        // Helper: highlight query inside a text segment
        const highlightText = (text: string, lineIdx: number): React.ReactNode => {
          if (!q) return text;
          const lower = text.toLowerCase();
          const parts: React.ReactNode[] = [];
          let cursor = 0;
          while (cursor < text.length) {
            const found = lower.indexOf(q, cursor);
            if (found === -1) { parts.push(text.slice(cursor)); break; }
            if (found > cursor) parts.push(text.slice(cursor, found));
            const isCurrent = lineIdx === currentMatchLineIdx;
            parts.push(
              <mark key={found} style={{
                backgroundColor: isCurrent ? '#f9e2af' : '#fab387',
                color: '#1e1e2e', borderRadius: '2px'
              }}>{text.slice(found, found + q.length)}</mark>
            );
            cursor = found + q.length;
          }
          return <>{parts}</>;
        };

        const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            if (totalMatches > 0) setXmlSearchCurrent(c => (c + 1) % totalMatches);
          } else if (e.key === 'Escape') {
            setXmlSearchQuery('');
          }
        };

        return (
          <div className="modal-overlay" onClick={() => { setShowXmlViewerModal(false); setXmlSearchQuery(''); setXmlSearchCurrent(0); }}>
            <div
              className="modal"
              onClick={e => e.stopPropagation()}
              style={{ width: '90vw', maxWidth: '1100px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0 }}
            >
              {/* Top bar: filename + close */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 16px', borderBottom: '1px solid #313244',
                backgroundColor: '#1e1e2e', borderRadius: '8px 8px 0 0', flexShrink: 0
              }}>
                <span style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#cdd6f4' }}>📄 {xmlViewerFilename}</span>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <a
                    href={`${BASE_URL}/user/cantiere/${cantiereId}/object/${objectName}/file/${metsFilename}?jwt=${localStorage.getItem('access_token')}`}
                    target="_blank" rel="noreferrer"
                    style={{ fontSize: '0.75rem', color: '#89b4fa', textDecoration: 'underline' }}
                  >Apri raw ↗</a>
                  <button
                    onClick={() => { setShowXmlViewerModal(false); setXmlSearchQuery(''); setXmlSearchCurrent(0); }}
                    style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#a6adc8', lineHeight: 1 }}
                    title="Chiudi"
                  >×</button>
                </div>
              </div>

              {/* Search bar */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '8px 12px', backgroundColor: '#181825',
                borderBottom: '1px solid #313244', flexShrink: 0
              }}>
                <input
                  ref={xmlSearchInputRef}
                  type="text"
                  placeholder="🔍 Cerca nel file XML… (Invio = avanti)"
                  value={xmlSearchQuery}
                  onChange={e => { setXmlSearchQuery(e.target.value); setXmlSearchCurrent(0); }}
                  onKeyDown={handleSearchKeyDown}
                  style={{
                    flex: 1, backgroundColor: '#1e1e2e',
                    border: '1px solid #45475a', borderRadius: '4px',
                    padding: '5px 10px', color: '#cdd6f4', fontFamily: 'monospace',
                    fontSize: '0.82rem', outline: 'none'
                  }}
                  autoFocus
                />
                {q && (
                  <>
                    <span style={{ color: totalMatches > 0 ? '#a6e3a1' : '#f38ba8', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {totalMatches > 0 ? `${xmlSearchCurrent % totalMatches + 1} / ${totalMatches}` : '0 risultati'}
                    </span>
                    <button onClick={() => setXmlSearchCurrent(c => c > 0 ? c - 1 : Math.max(0, totalMatches - 1))}
                      disabled={totalMatches === 0}
                      style={{ background: '#313244', border: 'none', color: '#cdd6f4', borderRadius: '3px', padding: '3px 8px', cursor: 'pointer' }}
                    >▲</button>
                    <button onClick={() => setXmlSearchCurrent(c => (c + 1) % Math.max(1, totalMatches))}
                      disabled={totalMatches === 0}
                      style={{ background: '#313244', border: 'none', color: '#cdd6f4', borderRadius: '3px', padding: '3px 8px', cursor: 'pointer' }}
                    >▼</button>
                    <button onClick={() => { setXmlSearchQuery(''); setXmlSearchCurrent(0); }}
                      style={{ background: 'none', border: 'none', color: '#a6adc8', fontSize: '1rem', cursor: 'pointer' }}
                      title="Cancella ricerca"
                    >✕</button>
                  </>
                )}
              </div>

              {/* Content */}
              <div style={{ flex: 1, overflow: 'auto', backgroundColor: '#1e1e2e', borderRadius: '0 0 8px 8px' }}>
                {xmlViewerLoading ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#a6adc8', fontFamily: 'monospace' }}>Caricamento...</div>
                ) : (
                  <table style={{
                    width: '100%', borderCollapse: 'collapse',
                    fontFamily: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
                    fontSize: '0.78rem', lineHeight: '1.65'
                  }}>
                    <tbody>
                      {lines.map((line, idx) => {
                        const isCurrentMatch = idx === currentMatchLineIdx;
                        const isAnyMatch = q && matchedLines.includes(idx);
                        return (
                          <tr
                            key={idx}
                            id={`xml-line-${idx}`}
                            ref={isCurrentMatch ? (el => el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })) : undefined}
                            style={{ verticalAlign: 'top', backgroundColor: isCurrentMatch ? '#2a2a3d' : undefined }}
                          >
                            <td style={{
                              width: '3.8em', minWidth: '3.8em', padding: '0 10px',
                              textAlign: 'right', userSelect: 'none',
                              color: isAnyMatch ? '#fab387' : '#45475a',
                              backgroundColor: '#181825', borderRight: '1px solid #313244'
                            }}>
                              {idx + 1}
                            </td>
                            <td style={{ padding: '0 14px', whiteSpace: 'pre', color: '#cdd6f4' }}>
                              {line.split(/(<\/?[^>]+>)/g).map((part, pi) =>
                                /^<\/?[^>]+>$/.test(part)
                                  ? <span key={pi} style={{ color: '#89b4fa' }}>{highlightText(part, idx)}</span>
                                  : <span key={pi}>{highlightText(part, idx)}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {isMonitoringOpen && (
        <MonitoringModal
          status={monitoringStatus}
          onClose={() => setIsMonitoringOpen(false)}
        />
      )}

      {isExifMonitoringOpen && (
        <ExifMonitoringModal
          status={exifMonitoringStatus}
          onClose={() => setIsExifMonitoringOpen(false)}
        />
      )}

      {isJpegMonitoringOpen && (
        <JpegMonitoringModal
          status={jpegMonitoringStatus}
          onClose={() => setIsJpegMonitoringOpen(false)}
        />
      )}

      {/* Lightbox Modal for JPEG viewer */}
      {lightboxOpen && filteredJpegFiles.length > 0 && (() => {
        const currentFile = filteredJpegFiles[lightboxIndex];
        const baseName = currentFile?.replace(/\.(jpg|jpeg)$/i, '');
        const matchingTiff = files.find(f => f.file_name.replace(/\.tiff?$/i, '') === baseName);
        const currentLabel = matchingTiff?.label;
        return (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.92)',
              zIndex: 9999,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
            }}
            onClick={(e) => { if (e.target === e.currentTarget) setLightboxOpen(false); }}
          >
            {/* Top bar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              width: '100%', padding: '8px 16px', color: '#fff',
              background: 'rgba(0,0,0,0.5)', flexShrink: 0
            }}>
              <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                {lightboxIndex + 1} / {filteredJpegFiles.length}
                {currentLabel && <span style={{ marginLeft: '12px', fontStyle: 'italic', color: '#ccc' }}>Label: {currentLabel}</span>}
              </span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => setLightboxZoom(z => Math.min(1.5, parseFloat((z + 0.25).toFixed(2))))}
                  disabled={lightboxZoom >= 1.5}
                  style={{ background: '#333', border: 'none', color: '#fff', padding: '4px 10px', borderRadius: '4px', cursor: lightboxZoom >= 1.5 ? 'not-allowed' : 'pointer', opacity: lightboxZoom >= 1.5 ? 0.5 : 1 }}
                  title="Zoom in (max 150%)"
                >
                  <i className="fas fa-search-plus" /> {Math.round(lightboxZoom * 100)}%
                </button>
                <button
                  onClick={() => setLightboxZoom(1)}
                  disabled={lightboxZoom === 1}
                  style={{ background: '#333', border: 'none', color: '#fff', padding: '4px 10px', borderRadius: '4px', cursor: lightboxZoom === 1 ? 'not-allowed' : 'pointer', opacity: lightboxZoom === 1 ? 0.5 : 1 }}
                  title="Reimposta zoom"
                >
                  <i className="fas fa-compress-arrows-alt" />
                </button>
                <button
                  onClick={() => setLightboxOpen(false)}
                  style={{ background: 'transparent', border: '1px solid #666', color: '#fff', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '1rem' }}
                  title="Chiudi"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Image area: 3-column layout so buttons stay fixed during zoom scroll */}
            <div style={{
              flex: 1, width: '100%',
              height: '85vh', maxHeight: '85vh',
              display: 'flex', alignItems: 'stretch',
              position: 'relative', overflow: 'hidden'
            }}>
              {/* Prev button column */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', flexShrink: 0 }}>
                <button
                  onClick={() => { setLightboxIndex(i => Math.max(0, i - 1)); setLightboxZoom(1); }}
                  disabled={lightboxIndex === 0}
                  style={{
                    background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff',
                    fontSize: '1.8rem', borderRadius: '50%', width: '44px', height: '44px',
                    cursor: lightboxIndex === 0 ? 'not-allowed' : 'pointer',
                    opacity: lightboxIndex === 0 ? 0.3 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  ‹
                </button>
              </div>

              {/* Scrollable image area */}
              <div style={{
                flex: 1,
                overflow: lightboxZoom > 1 ? 'auto' : 'hidden',
                // When zoomed, use block layout so scroll works in all directions.
                // flex centering clips top/left overflow preventing scroll.
                display: lightboxZoom > 1 ? 'block' : 'flex',
                alignItems: lightboxZoom > 1 ? undefined : 'center',
                justifyContent: lightboxZoom > 1 ? undefined : 'center',
              }}>
                <img
                  key={currentFile}
                  src={getFileUrl(currentFile, 'JPEG')}
                  alt={currentFile}
                  style={{
                    maxHeight: lightboxZoom === 1 ? '100%' : 'none',
                    maxWidth: lightboxZoom === 1 ? '100%' : 'none',
                    width: lightboxZoom > 1 ? `${lightboxZoom * 100}%` : 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    display: 'block',
                    transition: 'width 0.2s',
                    margin: lightboxZoom > 1 ? '0' : 'auto',
                  }}
                  onError={(e) => { (e.target as any).src = 'https://via.placeholder.com/800x600?text=Immagine+non+disponibile'; }}
                />
              </div>

              {/* Next button column */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px', flexShrink: 0 }}>
                <button
                  onClick={() => { setLightboxIndex(i => Math.min(filteredJpegFiles.length - 1, i + 1)); setLightboxZoom(1); }}
                  disabled={lightboxIndex === filteredJpegFiles.length - 1}
                  style={{
                    background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff',
                    fontSize: '1.8rem', borderRadius: '50%', width: '44px', height: '44px',
                    cursor: lightboxIndex === filteredJpegFiles.length - 1 ? 'not-allowed' : 'pointer',
                    opacity: lightboxIndex === filteredJpegFiles.length - 1 ? 0.3 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  ›
                </button>
              </div>
            </div>

            {/* Bottom filename */}
            <div style={{ color: '#aaa', fontSize: '0.8rem', padding: '6px 16px', flexShrink: 0 }}>
              {currentFile}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ObjectDashboard;